"use client"

import React, { useState } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"

export interface ImageWithFallbackProps {
  imageUrl?: string | null
  name?: string
  className?: string
  imgClassName?: string
  size?: number
  sizes?: string
  fit?: 'cover' | 'contain' | 'smart'
  objectPositionClass?: string
  rounded?: "full" | "lg" | "md" | "none"
  alt?: string
}

function getInitials(name?: string) {
  if (!name) return "EP"
  const parts = name.trim().split(/\s+/)
  const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("")
  return initials || "EP"
}

export default function ImageWithFallback({
  imageUrl,
  name,
  className,
  imgClassName,
  size = 144,
  sizes,
  fit = 'smart',
  objectPositionClass,
  rounded = "full",
  alt
}: ImageWithFallbackProps) {
  const [errored, setErrored] = useState(false)
  const [resolvedFit, setResolvedFit] = useState<'cover' | 'contain' | undefined>(
    fit === 'cover' ? 'cover' : fit === 'contain' ? 'contain' : undefined
  )
  const radiusClass =
    rounded === "full" ? "rounded-full" : rounded === "lg" ? "rounded-lg" : rounded === "md" ? "rounded-md" : "rounded-none"

  const containerStyle: React.CSSProperties | undefined =
    typeof size === "number" && size > 0 ? { width: size, height: size } : undefined
  const sizesAttr = sizes || (typeof size === "number" && size > 0 ? `${size}px` : "100vw")
  const hasExplicitFitClass = /object-(cover|contain)/.test(imgClassName || '')

  if (!imageUrl || errored) {
    return (
      <div
        className={cn("flex items-center justify-center bg-gray-200 text-gray-600", radiusClass, className)}
        {...(containerStyle && { style: containerStyle })}
        role="img"
        aria-label={alt || name || "Avatar"}
      >
        <span className="text-3xl font-semibold">{getInitials(name)}</span>
      </div>
    )
  }

  return (
    <div
      className={cn("relative overflow-hidden", radiusClass, className)}
      {...(containerStyle && { style: containerStyle })}
    >
      <Image
        src={imageUrl}
        alt={alt || `${name || "Profile"} headshot`}
        fill
        sizes={sizesAttr}
        className={cn(
          // Allow callers to pass their own object-fit; otherwise apply heuristic
          !hasExplicitFitClass && (resolvedFit === 'cover' ? 'object-cover' : resolvedFit === 'contain' ? 'object-contain' : 'object-cover'),
          objectPositionClass || 'object-center',
          imgClassName,
        )}
        onError={() => setErrored(true)}
        onLoad={(e: any) => {
          if (fit !== 'smart' || hasExplicitFitClass) return
          try {
            const img = e.target as HTMLImageElement
            const w = img.naturalWidth as number | undefined
            const h = img.naturalHeight as number | undefined
            if (!w || !h) return
            const ratio = w / h
            // Heuristic: Use contain only for extreme panoramas or ultra-tall shots
            // Treat typical 16:9 (~1.78) as cover for better card consistency
            if (ratio >= 2.0 || ratio <= 0.5) {
              setResolvedFit('contain')
            } else {
              setResolvedFit('cover')
            }
          } catch {}
        }}
      />
    </div>
  )
}

 
